import { useState, useEffect } from 'preact/hooks';
import { BonusTransactionsSkeleton } from './LoadingSkeletons';
import { t } from '../i18n';
import { getConfig } from '../config';
import { getBonusTransactions, type BonusTransactionRow } from '../api';
import { store } from '../store';
import { navigateToView } from '../navigation';
import { formatPrice } from '../utils/currency';

function typeLabel(type: string | undefined): string {
  switch (type) {
    case 'M': return t('bonusTx.typeM');
    case 'C': return t('bonusTx.typeC');
    case 'O': return t('bonusTx.typeO');
    case 'P': return t('bonusTx.typeP');
    case 'W': return t('bonusTx.typeW');
    default: return type ?? '';
  }
}

export function BonusTransactionsPage() {
  const config = getConfig();
  const [list, setList] = useState<BonusTransactionRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const auth = store.getAuth();
  const balance = Math.max(0, Number(auth?.bonus_account) || 0);

  useEffect(() => {
    getBonusTransactions(config).then((res) => {
      setLoading(false);
      if (res.status === 200 && res.result) setList(Array.isArray(res.result) ? res.result : []);
    });
  }, []);

  if (loading) {
    return <BonusTransactionsSkeleton />;
  }

  const rows = list ?? [];

  return (
    <div class="sw-tickets-shell bonus-transactions-shell">
      <div class="shop-widget-bonus-transactions sw-surface-card bonus-transactions-card">
        <div class="bonus-transactions-card__header">
          <button type="button" class="btn btn-outline-secondary btn-sm bonus-transactions-card__back-btn" onClick={() => navigateToView('bonus-account')}>
            <span style="display:inline-flex;align-items:center;gap:.35rem">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {t('bonusTx.back')}
            </span>
          </button>
          <div class="bonus-transactions-card__balance">
            <span>{t('bonusTx.balance')}</span>
            <strong>{formatPrice(balance)}</strong>
          </div>
        </div>

        {rows.length === 0 ? (
          <div class="bonus-transactions-card__empty">{t('bonusTx.empty')}</div>
        ) : (
          <>
            <div class="table-responsive bonus-transactions-table-wrap">
              <table class="table bonus-transactions-table">
                <thead>
                  <tr>
                    <th>{t('bonusTx.date')}</th>
                    <th>{t('bonusTx.addedBonus')}</th>
                    <th>{t('bonusTx.usedBonus')}</th>
                    <th>{t('bonusTx.type')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((tx, k) => (
                    <tr key={`${tx.date ?? 'tx'}-${k}`}>
                      <td>{tx.date ?? '-'}</td>
                      <td class="bonus-transactions-table__credit">{(tx.credit != null && Number(tx.credit) > 0) ? formatPrice(tx.credit) : '-'}</td>
                      <td class="bonus-transactions-table__debit">{(tx.debit != null && Number(tx.debit) > 0) ? formatPrice(tx.debit) : '-'}</td>
                      <td>
                        {tx.type === 'O' && tx.sorderid ? t('bonusTx.typeOOrder', { orderId: tx.sorderid }) : typeLabel(tx.type)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div class="bonus-transactions-mobile">
              {rows.map((tx, k) => (
                <div class="bonus-transactions-mobile__card" key={`m-${tx.date ?? 'tx'}-${k}`}>
                  <div class="bonus-transactions-mobile__row">
                    <span>{t('bonusTx.date')}</span>
                    <strong>{tx.date ?? '-'}</strong>
                  </div>
                  <div class="bonus-transactions-mobile__row">
                    <span>{t('bonusTx.addedBonus')}</span>
                    <strong class="bonus-transactions-table__credit">{(tx.credit != null && Number(tx.credit) > 0) ? formatPrice(tx.credit) : '-'}</strong>
                  </div>
                  <div class="bonus-transactions-mobile__row">
                    <span>{t('bonusTx.usedBonus')}</span>
                    <strong class="bonus-transactions-table__debit">{(tx.debit != null && Number(tx.debit) > 0) ? formatPrice(tx.debit) : '-'}</strong>
                  </div>
                  <div class="bonus-transactions-mobile__row">
                    <span>{t('bonusTx.type')}</span>
                    <strong>{tx.type === 'O' && tx.sorderid ? t('bonusTx.typeOOrder', { orderId: tx.sorderid }) : typeLabel(tx.type)}</strong>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
